import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { logger } from './logger';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Clock,
  Loader,
  ShieldCheck,
} from 'lucide-react';
import { JobStatus, ValidationStatus } from '~/generated/prisma';
import { toast } from 'sonner';

/**
 * Copy text to clipboard
 * @param text - The text to copy
 * @param message - The message to display when the text is copied
 * @returns void
 */
export const copyToClipboard = async (
  text: string,
  message: string = 'Copied to clipboard!',
) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(message);
  } catch (error) {
    logger.error('Error copying to clipboard:', { error });
    toast.error('Failed to copy to clipboard');
  }
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const getJobStatusBadge = (status: JobStatus) => {
  switch (status) {
    case JobStatus.COMPLETED:
      return (
        <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center shrink-0">
          <CheckCircle className="w-5 h-5 text-green-500" />
        </div>
      );
    case JobStatus.PROCESSING:
      return (
        <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center shrink-0">
          <Loader className="w-5 h-5 text-blue-500 animate-spin" />
        </div>
      );
    case JobStatus.PENDING:
      return (
        <div className="w-10 h-10 bg-yellow-500/10 rounded-lg flex items-center justify-center shrink-0">
          <Clock className="w-5 h-5 text-yellow-500" />
        </div>
      );
    case JobStatus.FAILED:
      return (
        <div className="w-10 h-10 bg-red-500/10 rounded-lg flex items-center justify-center shrink-0">
          <AlertTriangle className="w-5 h-5 text-red-500" />
        </div>
      );
    default:
      return (
        <div className="w-10 h-10 bg-[#10b981]/10 rounded-lg flex items-center justify-center shrink-0">
          <CheckCircle className="w-5 h-5 text-[#10b981]" />
        </div>
      );
  }
};

export const getJobStatusBadgeWithLabel = (status: JobStatus) => {
  switch (status) {
    case JobStatus.COMPLETED:
      return (
        <div className="px-2 h-8 bg-green-500/10 rounded-sm flex items-center justify-center shrink-0 gap-2">
          <CheckCircle className="w-5 h-5 text-green-500" />
          <span className="text-sm font-medium sm:text-sm text-green-500">
            Completed
          </span>
        </div>
      );
    case JobStatus.PROCESSING:
      return (
        <div className="px-2 h-8 bg-blue-500/10 rounded-sm flex items-center justify-center shrink-0 gap-2">
          <Loader className="w-5 h-5 text-blue-500 animate-spin" />
          <span className="text-sm font-medium sm:text-sm text-blue-500">
            Processing
          </span>
        </div>
      );
    case JobStatus.PENDING:
      return (
        <div className="px-2 h-8 bg-yellow-500/10 rounded-sm flex items-center justify-center shrink-0 gap-2">
          <Clock className="w-5 h-5 text-yellow-500" />
          <span className="text-sm font-medium sm:text-sm text-yellow-500">
            Pending
          </span>
        </div>
      );
    case JobStatus.FAILED:
      return (
        <div className="px-2 h-8 bg-red-500/10 rounded-sm flex items-center justify-center shrink-0 gap-2">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          <span className="text-sm font-medium sm:text-sm text-red-500">
            Failed
          </span>
        </div>
      );
    default:
      return (
        <div className="px-2 h-8 bg-[#10b981]/10 rounded-sm flex items-center justify-center shrink-0 gap-2">
          <CheckCircle className="w-5 h-5 text-[#10b981]" />
          <span className="text-sm font-medium sm:text-sm text-[#10b981]">
            {status}
          </span>
        </div>
      );
  }
};

export const getValidationStatusBadge = (status: ValidationStatus) => {
  switch (status) {
    case ValidationStatus.COMPLETED:
      return (
        <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center shrink-0">
          <CheckCircle className="w-5 h-5 text-green-500" />
        </div>
      );
    case ValidationStatus.VALIDATING:
      return (
        <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center shrink-0">
          <Loader className="w-5 h-5 text-blue-500 animate-spin" />
        </div>
      );
    case ValidationStatus.PENDING:
      return (
        <div className="w-10 h-10 bg-yellow-500/10 rounded-lg flex items-center justify-center shrink-0">
          <Clock className="w-5 h-5 text-yellow-500" />
        </div>
      );
    case ValidationStatus.FAILED:
      return (
        <div className="w-10 h-10 bg-red-500/10 rounded-lg flex items-center justify-center shrink-0">
          <AlertTriangle className="w-5 h-5 text-red-500" />
        </div>
      );
    default:
      return (
        <div className="w-10 h-10 bg-[#10b981]/10 rounded-lg flex items-center justify-center shrink-0">
          <CheckCircle className="w-5 h-5 text-[#10b981]" />
        </div>
      );
  }
};

export const getValidationStatusLabel = (status: ValidationStatus) => {
  switch (status) {
    case ValidationStatus.COMPLETED:
      return (
        <span className="inline-flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-green-500/10 text-green-500 rounded-lg text-xs sm:text-sm">
          <ShieldCheck className="w-3 h-3 sm:w-4 sm:h-4" />
          <span>Completed</span>
        </span>
      );
    case ValidationStatus.VALIDATING:
      return (
        <span className="inline-flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-blue-500/10 text-blue-500 rounded-lg text-xs sm:text-sm">
          <Loader className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" />
          <span className="hidden xs:inline">Validating...</span>
          <span className="xs:hidden">Validating</span>
        </span>
      );
    case ValidationStatus.PENDING:
      return (
        <span className="inline-flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-yellow-500/10 text-yellow-500 rounded-lg text-xs sm:text-sm">
          <ShieldCheck className="w-3 h-3 sm:w-4 sm:h-4" />
          <span className="hidden xs:inline">Pending</span>
          <span className="xs:hidden">Pending</span>
        </span>
      );
    case ValidationStatus.FAILED:
      return (
        <span className="inline-flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-red-500/10 text-red-500 rounded-lg text-xs sm:text-sm">
          <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4" />
          <span>Failed</span>
        </span>
      );
    default:
      return null;
  }
};
